import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { usePageMeta } from '../hooks/usePageMeta';

type Status = 'confirm' | 'submitting' | 'success' | 'error';

const EXPIRED =
  'This unsubscribe link is invalid or has expired. Use the Unsubscribe link in your most recent Food Safety Digest email.';
const FAILED = 'Something went wrong. Please try again later.';

// Opening this page changes nothing. Link scanners and anyone a digest was
// forwarded to open it too, so only the button unsubscribes. It POSTs to the
// endpoint that mail clients use for one-click unsubscribe (RFC 8058).
export default function UnsubscribePage() {
  usePageMeta({ title: 'Unsubscribe', noindex: true });
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>(token ? 'confirm' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Missing unsubscribe token.');

  async function unsubscribe() {
    if (!token) return;
    setStatus('submitting');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/public/unsubscribe?token=${encodeURIComponent(token)}`,
        { method: 'POST' },
      );
      if (res.ok) {
        setStatus('success');
        return;
      }
      setStatus('error');
      setMessage(res.status === 400 ? EXPIRED : FAILED);
    } catch {
      setStatus('error');
      setMessage(FAILED);
    }
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-card" style={{ textAlign: 'center' }}>
        <div className="subscribe-card__logo">FS</div>
        <h1 className="subscribe-card__title">Food Safety Digest</h1>

        {(status === 'confirm' || status === 'submitting') && (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-primary)', margin: '0 0 8px' }}>
              Unsubscribe from all emails?
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', margin: '0 0 8px' }}>
              This stops every Food Safety Digest email to your address: the daily RASFF digest,
              the weekly digest, and incident and legislation alerts.
            </p>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', margin: '0 0 20px' }}>
              To get fewer emails instead, use Manage preferences at the bottom of any recent email.
            </p>
            <button
              type="button"
              className="subscribe-card__cta"
              onClick={unsubscribe}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Unsubscribing…' : 'Unsubscribe'}
            </button>
            <p style={{ marginTop: 16 }}>
              <Link to="/" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                Keep my subscription
              </Link>
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="subscribe-success" style={{ marginTop: 24 }}>
            <div className="subscribe-success__icon">&#10003;</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-primary)', margin: '12px 0 8px' }}>
              Unsubscribed
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
              You will not receive any more Food Safety Digest emails.
            </p>
            <p style={{ marginTop: 16 }}>
              <Link to="/subscribe/form" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                Re-subscribe
              </Link>
            </p>
          </div>
        )}

        {status === 'error' && (
          <div style={{ marginTop: 24 }}>
            <p role="alert" style={{ color: '#C44536', fontSize: '0.9rem', fontWeight: 600 }}>{message}</p>
            <p style={{ marginTop: 16 }}>
              <Link to="/" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                Back to home
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
